import React from "react";
import {
 Modal,
 Row,
 Col,
 Badge,
 Table,
 Button,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import PermissionWrapper from "../PermissionWrapper";
import { getFileUrl } from "../../../api";

/* Constants */
const STATUS_COLORS = {
 Draft: "secondary",
 Submitted: "warning",
 "Under Investigation": "info",
 Resolved: "success",
 Closed: "dark",
};

const SEVERITY_COLORS = {
 Low: "success",
 Medium: "warning",
 High: "danger",
 Critical: "dark",
};

const CA_STATUS_COLORS = {
 Open: "warning",
 "In Progress": "info",
 Completed: "success",
 Overdue: "danger",
};

const COMPLIANT_COLORS = {
 Yes: "success",
 No: "danger",
 NA: "secondary",
 Pending: "warning",
};

/* Field Row helper */
const Field = ({ label, value, className = "" }) => (
 <div className={`mb-2 ${className}`}>
 <small className="text-muted fw-semibold d-block">{label}</small>
 <span>{value || "—"}</span>
 </div>
);

/* Component */
const SafetyViewModal = ({ show, record, onHide, onRefresh, user }) => {
 const navigate = useNavigate();

 if (!record) return null;

 const fmt = (date) =>
 date ? new Date(date).toLocaleDateString("en-IN") : "—";

 return (
 <Modal show={show} onHide={onHide} size="xl" scrollable>
 <Modal.Header closeButton>
 <Modal.Title>
 <i className="fas fa-hard-hat me-2 text-warning" />
 {record.incidentNumber} — Safety Incident
 </Modal.Title>
 </Modal.Header>

 <Modal.Body>
 {/* Status Banner */}
 <div className="d-flex gap-2 align-items-center mb-4 flex-wrap">
 <Badge bg={STATUS_COLORS[record.status] || "secondary"} style={{ fontSize: "0.9rem" }}>
 {record.status}
 </Badge>
 <Badge bg={SEVERITY_COLORS[record.severity] || "secondary"} style={{ fontSize: "0.9rem" }}>
 {record.severity} Severity
 </Badge>
 {record.riskRating && (
 <Badge bg={SEVERITY_COLORS[record.riskRating] || "secondary"} style={{ fontSize: "0.9rem" }}>
 Risk: {record.riskRating}
 </Badge>
 )}
 <span className="text-muted ms-auto" style={{ fontSize: "0.8rem" }}>
 Reported: {fmt(record.reportedDate)}
 </span>
 </div>

 {/* Section 1: Incident Details */}
 <h6 className="text-primary border-bottom pb-1 mb-3">
 <i className="fas fa-info-circle me-1" /> Incident Details
 </h6>
 <Row className="mb-3">
 <Col md={3}><Field label="Incident No." value={record.incidentNumber} /></Col>
 <Col md={2}><Field label="Date" value={fmt(record.incidentDate)} /></Col>
 <Col md={2}><Field label="Time" value={record.incidentTime} /></Col>
 <Col md={3}><Field label="Type" value={record.incidentType} /></Col>
 <Col md={2}><Field label="Severity" value={record.severity} /></Col>
 </Row>
 <Row className="mb-3">
 <Col md={4}>
 <Field
 label="Location"
 value={
 <span>
 <i className="fas fa-map-marker-alt me-1 text-muted" />
 {record.location}
 </span>
 }
 />
 </Col>
 <Col md={4}>
 <Field label="Project" value={record.project?.projectName || record.project?.name} />
 </Col>
 <Col md={4}>
 <Field label="Work Order" value={record.workOrder?.woNo} />
 </Col>
 </Row>
 <Row className="mb-3">
 <Col md={12}>
 <Field label="Description" value={record.description} />
 </Col>
 </Row>
 <Row>
 <Col md={4}>
 <Field label="Reported By" value={record.reportedBy?.name} />
 </Col>
 <Col md={4}>
 <Field label="Investigated By" value={record.investigatedBy?.name} />
 </Col>
 <Col md={4}>
 <Field
 label="Investigation Required"
 value={record.investigationRequired ? "Yes" : "No"}
 />
 </Col>
 </Row>

 {/* Section 2: Affected Person */}
 {(record.involvedPerson || record.injuryType) && (
 <>
 <h6 className="text-danger border-bottom pb-1 mt-4 mb-3">
 <i className="fas fa-user-injured me-1" /> Affected Person
 </h6>
 <Row className="mb-3">
 <Col md={3}><Field label="Name" value={record.involvedPerson} /></Col>
 <Col md={2}><Field label="Designation" value={record.designation} /></Col>
 <Col md={3}><Field label="Employer" value={record.employer} /></Col>
 <Col md={2}><Field label="Injury Type" value={record.injuryType} /></Col>
 <Col md={2}><Field label="Body Part" value={record.bodyPartAffected} /></Col>
 </Row>
 {record.medicalTreatmentProvided && (
 <Row>
 <Col md={12}>
 <Field
 label="Medical Treatment Details"
 value={record.medicalDetails}
 />
 </Col>
 </Row>
 )}
 </>
 )}

 {/* Section 3: Cause Analysis */}
 {(record.immediateCause || record.rootCause) && (
 <>
 <h6 className="text-info border-bottom pb-1 mt-4 mb-3">
 <i className="fas fa-search me-1" /> Cause Analysis
 </h6>
 <Row className="mb-3">
 <Col md={6}><Field label="Immediate Cause" value={record.immediateCause} /></Col>
 <Col md={6}><Field label="Root Cause" value={record.rootCause} /></Col>
 </Row>
 <Row>
 <Col md={6}>
 <Field label="Contributing Factors" value={record.contributingFactors} />
 </Col>
 <Col md={6}>
 <Field label="Preventive Measures" value={record.preventiveMeasures} />
 </Col>
 </Row>
 </>
 )}

 {/* Section 4: Risk Assessment */}
 {(record.likelihoodScore || record.consequenceScore) && (
 <>
 <h6 className="text-warning border-bottom pb-1 mt-4 mb-3">
 <i className="fas fa-exclamation-triangle me-1" /> Risk Assessment
 </h6>
 <Row>
 <Col md={3}>
 <Field
 label="Likelihood Score"
 value={record.likelihoodScore ? `${record.likelihoodScore} / 5` : null}
 />
 </Col>
 <Col md={3}>
 <Field
 label="Consequence Score"
 value={record.consequenceScore ? `${record.consequenceScore} / 5` : null}
 />
 </Col>
 <Col md={3}>
 <Field
 label="Risk Score"
 value={
 record.likelihoodScore && record.consequenceScore
 ? record.likelihoodScore * record.consequenceScore
 : null
 }
 />
 </Col>
 <Col md={3}>
 {record.riskRating && (
 <div>
 <small className="text-muted fw-semibold d-block">Risk Rating</small>
 <Badge bg={SEVERITY_COLORS[record.riskRating] || "secondary"}>
 {record.riskRating}
 </Badge>
 </div>
 )}
 </Col>
 </Row>
 </>
 )}

 {/* Section 5: Corrective Actions */}
 {record.correctiveActions?.length > 0 && (
 <>
 <h6 className="text-primary border-bottom pb-1 mt-4 mb-3">
 <i className="fas fa-tasks me-1" /> Corrective Actions ({record.correctiveActions.length})
 </h6>
 <div className="table-responsive">
 <Table size="sm" hover className="align-middle">
 <thead className="table-light">
 <tr>
 <th>Action</th>
 <th>Assigned To</th>
 <th>Due Date</th>
 <th>Completed</th>
 <th>Status</th>
 <th>Remarks</th>
 </tr>
 </thead>
 <tbody>
 {record.correctiveActions.map((ca, i) => (
 <tr key={ca._id || i}>
 <td>{ca.action}</td>
 <td>{ca.assignedTo?.name || "—"}</td>
 <td>{fmt(ca.dueDate)}</td>
 <td>{fmt(ca.completedDate)}</td>
 <td>
 <Badge bg={CA_STATUS_COLORS[ca.status] || "secondary"}>
 {ca.status}
 </Badge>
 </td>
 <td>{ca.remarks || "—"}</td>
 </tr>
 ))}
 </tbody>
 </Table>
 </div>
 </>
 )}

 {/* Section 6: Compliance Checklist */}
 {record.checklistItems?.length > 0 && (
 <>
 <h6 className="text-success border-bottom pb-1 mt-4 mb-3">
 <i className="fas fa-clipboard-check me-1" /> Compliance Checklist ({record.checklistItems.length})
 </h6>
 <div className="table-responsive">
 <Table size="sm" hover className="align-middle">
 <thead className="table-light">
 <tr>
 <th>#</th>
 <th>Item</th>
 <th style={{ width: 110 }}>Compliant</th>
 <th>Remarks</th>
 </tr>
 </thead>
 <tbody>
 {record.checklistItems.map((item, i) => (
 <tr key={item._id || i}>
 <td>{i + 1}</td>
 <td>{item.item}</td>
 <td>
 <Badge bg={COMPLIANT_COLORS[item.compliant] || "secondary"}>
 {item.compliant}
 </Badge>
 </td>
 <td>{item.remarks || "—"}</td>
 </tr>
 ))}
 </tbody>
 </Table>
 </div>
 </>
 )}

 {/* Section 7: Witnesses */}
 {record.witnesses?.length > 0 && (
 <>
 <h6 className="text-secondary border-bottom pb-1 mt-4 mb-3">
 <i className="fas fa-users me-1" /> Witnesses ({record.witnesses.length})
 </h6>
 <div className="table-responsive">
 <Table size="sm" hover className="align-middle">
 <thead className="table-light">
 <tr>
 <th>Name</th>
 <th>Designation</th>
 <th>Statement</th>
 </tr>
 </thead>
 <tbody>
 {record.witnesses.map((w, i) => (
 <tr key={w._id || i}>
 <td>{w.name || "—"}</td>
 <td>{w.designation || "—"}</td>
 <td>{w.statement || "—"}</td>
 </tr>
 ))}
 </tbody>
 </Table>
 </div>
 </>
 )}

 {/* Section 8: Toolbox Talk */}
 {record.incidentType === "Toolbox Talk" && record.toolboxTalkTopic && (
 <>
 <h6 className="text-primary border-bottom pb-1 mt-4 mb-3">
 <i className="fas fa-chalkboard-teacher me-1" /> Toolbox Talk
 </h6>
 <Row className="mb-3">
 <Col md={6}><Field label="Topic" value={record.toolboxTalkTopic} /></Col>
 <Col md={3}><Field label="Total Attendees" value={record.attendeesCount} /></Col>
 </Row>
 {record.attendees?.length > 0 && (
 <div className="table-responsive">
 <Table size="sm" hover className="align-middle">
 <thead className="table-light">
 <tr>
 <th>Name</th>
 <th>Designation</th>
 </tr>
 </thead>
 <tbody>
 {record.attendees.map((a, i) => (
 <tr key={i}>
 <td>{a.name}</td>
 <td>{a.designation || "—"}</td>
 </tr>
 ))}
 </tbody>
 </Table>
 </div>
 )}
 </>
 )}

 {/* Section 9: Lessons Learned */}
 {record.lessonsLearned && (
 <>
 <h6 className="text-warning border-bottom pb-1 mt-4 mb-3">
 <i className="fas fa-lightbulb me-1" /> Lessons Learned
 </h6>
 <p>{record.lessonsLearned}</p>
 </>
 )}

 {/* Section 10: Photos */}
 {record.photos?.length > 0 && (
 <>
 <h6 className="border-bottom pb-1 mt-4 mb-3">
 <i className="fas fa-images me-1" /> Photos / Evidence ({record.photos.length})
 </h6>
 <div className="d-flex flex-wrap gap-2">
 {record.photos.map((photo) => (
 <div
 key={photo._id}
 className="border rounded p-1"
 style={{ width: 110 }}
 >
 <a
 href={getFileUrl(photo.fileUrl)}
 target="_blank"
 rel="noopener noreferrer"
 >
 <img
 src={getFileUrl(photo.fileUrl)}
 alt={photo.fileName}
 className="img-fluid rounded"
 style={{ height: 80, objectFit: "cover", width: "100%" }}
 onError={(e) => {
 e.currentTarget.onerror = null;
 e.currentTarget.src = "/logo192.png";
 }}
 />
 </a>
 {photo.description && (
 <small className="text-muted d-block mt-1 text-truncate">
 {photo.description}
 </small>
 )}
 </div>
 ))}
 </div>
 </>
 )}

 {/* Approval Info */}
 {(record.approvedBy || record.approvalRemarks) && (
 <div className="alert alert-light border mt-4 mb-0">
 <small className="text-muted">
 <strong>Resolved by:</strong> {record.approvedBy?.name || "—"} on{" "}
 {fmt(record.approvalDate)}
 {record.approvalRemarks && (
 <span> — {record.approvalRemarks}</span>
 )}
 </small>
 </div>
 )}
 </Modal.Body>

 <Modal.Footer className="justify-content-between">
 <small className="text-muted">
 Created: {fmt(record.createdAt)} &nbsp;|&nbsp; Updated: {fmt(record.updatedAt)}
 </small>
 <div className="d-flex gap-2">
 <PermissionWrapper
 user={user}
 required={["edit:safety"]}
 mode="some"
 >
 <Button
 size="sm"
 variant="outline-primary"
 onClick={() => {
 onHide();
 navigate(`/safety/edit/${record._id}`);
 }}
 >
 <i className="fas fa-edit me-1" /> Edit
 </Button>
 </PermissionWrapper>
 <Button variant="secondary" size="sm" onClick={onHide}>
 Close
 </Button>
 </div>
 </Modal.Footer>
 </Modal>
 );
};

export default SafetyViewModal;
